import CustomerRefundsApiV2 from 'api/v2/admins/customer-refunds-api-v2'
import CardLoader from 'components/organisms/card-loader'
import GenericModal from 'components/organisms/generic-modal'
import { ALERT_TYPES } from 'constants/alert-constants'
import { STATUS } from 'domains/customer-order/constants'
import {
  CustomerOrderItemPdf
} from 'domains/customer-order/organisms'
import CustomerOrderItemListTable from 'domains/customer-order/organisms/item-list-table'
import CustomerOrderItemCard from 'domains/customer-order/organisms/item-card'
import CustomerOrderHistoryCard from 'domains/customer-order/molecules/history-card'
import CustomerOrderRefundForm from 'domains/customer-order/organisms/refund-form'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import {
  Card,
  CardHeader,
  Row,
  Col
} from 'reactstrap'
import { saveAs } from 'file-saver'
import { UncontrolledButtonDropdown, DropdownMenu, DropdownItem, DropdownToggle } from 'reactstrap'
import ZaloraApi from 'api/admins/zalora-api'
import { DOCUMENT_TYPES } from 'domains/zalora/zalora-constants'
import { LoaderButton } from 'components/molecules'
import { AFFILIATE_NAMES, DOCUMENT_TEMPLATES } from 'domains/customer-order/constants/customer-order-constants'

const STATUS_INVOICE_PRINTABLE = [
  STATUS.PROCESSING,
  STATUS.SHIPPED,
  STATUS.COMPLETED
]

const HIDDEN_HISTORY_KEYS = [
  'id',
  'email',
  'total',
  'is_cod',
  'number',
  'quantity',
  'sub_total',
  'created_at',
  'magento_id',
  'customer_id',
  'refund_total',
  'discount_code',
  'discount_total',
  'item_tax_total',
  'item_price_total',
  'item_weight_total',
  'item_discount_total',
  'magento_customer_id',
  'shipment_cost_total',
  'shipping_address_id',
  'shipment_discount_total',
  'payment_method_rate_total',
  'status',
  'updated_at'
]

const propTypes = {
  id    : PropTypes.string,
  order : PropTypes.shape({
    id              : PropTypes.number,
    number          : PropTypes.string,
    customer        : PropTypes.object,
    order_items     : PropTypes.array,
    status          : PropTypes.string,
    histories       : PropTypes.array,
    affiliate_name  : PropTypes.string,
    zalora_order_id : PropTypes.number
  }),
  isLoading                : PropTypes.bool,
  handleFetchCustomerOrder : PropTypes.func,
  pageUtils                : PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const CustomerOrderDetailItemPage = ({
  order,
  isLoading,
  handleFetchCustomerOrder,
  pageUtils
}) => {
  const [ selectedOrderItem, setSelectedOrderItem ]             = useState({})
  const [ isModalOpen, setIsModalOpen ]                         = useState(false)
  const [ isDownloading, setIsDownloading ]                     = useState(false)
  const [ selectedInvoiceTemplate, setSelectedInvoiceTemplate ] = useState(AFFILIATE_NAMES.TINKERLUST)

  const showInvoiceButton = STATUS_INVOICE_PRINTABLE.includes(order?.status)

  useEffect(() => {
    const isZaloraOrder = order.affiliate_name === AFFILIATE_NAMES.ZALORA
    if (isZaloraOrder) {
      setSelectedInvoiceTemplate(AFFILIATE_NAMES.ZALORA)
    }
  }, [ order.affiliate_name ])

  /**
   * Handler to select order item in Customer Order Item table
   * @param {object} datum - Selected Order Item record
   */
  const handleSelectedOrderItem = (datum) => {
    setIsModalOpen(true)
    setSelectedOrderItem(datum)
  }

  /** Display/hide refund form modal */
  const handleToggleModal = () => setIsModalOpen((prev) => !prev)

  /**
   * Handler Refund request submit
   * ! Since we don't pass this handler directly to the Formik onSubmit
   * ! we need to setSubmitting manually
   * @param {object} values
   * @param {object} formikBag
   */
  const handleSubmitForm = async (values, formikBag) => {
    formikBag.setSubmitting(true)

    await CustomerRefundsApiV2.create({ selectedOrderItem, values })
      .then(() => {
        setIsModalOpen(false)
        formikBag.resetForm()
        pageUtils.setAlertMsg('the item is ready to the refund process', ALERT_TYPES.SUCCESS)

        handleFetchCustomerOrder()
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => formikBag.setSubmitting(false))
  }

  /** Handle print Order Invoice with Tinkerlust Template */
  const handlePrintTinkerlustInvoice = () => {
    //get printable html
    const printableElements   = document.getElementById('printOrderInvoice').innerHTML
    const orderInvoicePdfHTML = `<html><head><title></title></head><body>${printableElements}</body></html>`
    document.body.innerHTML   = orderInvoicePdfHTML
    document.title            = `Customer Order Item - ${ order.customer.name } - ${ order.number }`
    window.print()

    //reload page when cancel/save/print pdf to back to the shipment detail page
    window.location.reload()
  }

  /**
   * Handles the download of non Tinkerlust template Invoice and convert it to PDF.
   */
  const handleDownloadInvoice = () => {
    setIsDownloading(true)
    ZaloraApi.downloadDocument({
      orderId      : order.id,
      documentType : DOCUMENT_TYPES.INVOICE
    })
      .then((response) => {
        const blob     = new Blob([ response.data ], { type: 'application/pdf' })
        const fileName = `zalora-order-invoice-${ order.number }.pdf`
        saveAs(blob, fileName)
      }).catch((error) => {
        // adding this logic because in `.downloadInvoice` we add responseType in axios
        // if we remove responseType axios the pdf will be blank
        if (error.response && error.response.data instanceof Blob) {
          const reader  = new FileReader()
          reader.onload = () => {
            const errorResponseData = JSON.parse(reader.result)
            pageUtils.setApiErrorMsg(errorResponseData)
          }
          reader.readAsText(error.response.data)
        } else {
          pageUtils.setApiErrorMsg(error.response.data)
        }
      }).finally(() => setIsDownloading(false))
  }

  const handlePrintInvoice = (template) => {
    switch (template) {
      case DOCUMENT_TEMPLATES.ZALORA:
        handleDownloadInvoice()
        break
      default:
        handlePrintTinkerlustInvoice()
    }
  }

  const CustomerOrderHistoryCardWithLoader = withLoader(CustomerOrderHistoryCard, CardLoader)

  return (
    <>
      <Card>
        <CardHeader
          tag={ 'h3' }
          className='d-flex justify-content-between'
        >
          { 'Items' }
          {
            showInvoiceButton && (
              <div className='d-flex flex-column align-items-end'>
                {
                  <UncontrolledButtonDropdown>
                    <DropdownToggle color='primary' className='mb-3 text-capitalize' caret>
                      { selectedInvoiceTemplate || 'Select Invoice Template' }
                    </DropdownToggle>
                    <DropdownMenu>
                      {
                        Object.values(DOCUMENT_TEMPLATES).map((template) => {
                          const isTinkerlustTemplate    = template === DOCUMENT_TEMPLATES.TINKERLUST
                          const isAffiliateNameTemplate = template === order.affiliate_name

                          if (isTinkerlustTemplate || isAffiliateNameTemplate) {
                            return (
                              <DropdownItem
                                key={ `invoice-template-${template}` }
                                className='text-capitalize'
                                onClick={ () => setSelectedInvoiceTemplate(template) }
                              >
                                { template }
                              </DropdownItem>
                            )
                          }
                        })
                      }
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>
                }

                <LoaderButton
                  color='info'
                  isLoading={ isDownloading }
                  disabled={ isDownloading }
                  onClick={ () => handlePrintInvoice(selectedInvoiceTemplate) }
                  text={ 'Print Invoice' }
                />
              </div>
            )
          }

        </CardHeader>
        <CustomerOrderItemListTable
          data={ order.order_items }
          handleClickAddToRefund={ handleSelectedOrderItem }
        />
      </Card>
      <CustomerOrderHistoryCardWithLoader
        isLoading={ isLoading }
        histories={ order.histories }
        hiddenContents={ HIDDEN_HISTORY_KEYS }
      />

      <GenericModal
        isOpen={ isModalOpen }
        toggle={ () => handleToggleModal(setIsModalOpen) }
        title={ 'Refund Request' }
        size='lg'
      >
        <Row>
          <Col xs={ 12 } md={ 6 }>
            <CustomerOrderItemCard orderItem={ selectedOrderItem }/>
          </Col>

          <Col xs={ 12 } md={ 6 }>
            <CustomerOrderRefundForm
              selectedOrderItem={ selectedOrderItem }
              onSubmitForm={ handleSubmitForm }
            />
          </Col>
        </Row>
      </GenericModal>
      {
        !isLoading
          ? <CustomerOrderItemPdf order={ order } />
          : null
      }
    </>
  )
}

CustomerOrderDetailItemPage.propTypes = propTypes

export default CustomerOrderDetailItemPage
